import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CoachingRecordInput, ResourceUploadOperationRequest } from "../services/coaching-dtos";
import { getCoachingServices } from "../services/coaching-service-provider";
import { cacheResourceLifecycleReadback, invalidateArtifactReadbacks } from "./coaching-write-truth-caches";
import { createIdempotencyKey, getMutationSideEffectSummary } from "./useCoachingQueries";

const services = getCoachingServices();

export function useArtifactWorkflowMutations() {
  const queryClient = useQueryClient();

  return {
    sideEffectSummary: {
      createResource: getMutationSideEffectSummary("resources.create"),
      updateResource: getMutationSideEffectSummary("resources.update"),
      completeUpload: getMutationSideEffectSummary("resources.completeUpload"),
      requestDownload: getMutationSideEffectSummary("resources.requestDownload"),
      deleteUpload: getMutationSideEffectSummary("resources.deleteUpload")
    },
    createResource: useMutation({
      mutationFn: (body: CoachingRecordInput) => services.resources.create(body, createIdempotencyKey("resources-create")),
      onSuccess: (resource) => cacheResourceLifecycleReadback(queryClient, resource.id, resource)
    }),
    updateResource: useMutation({
      mutationFn: ({ resourceId, body }: { resourceId: string; body: CoachingRecordInput }) =>
        services.resources.update(resourceId, body, createIdempotencyKey(`resources-update:${resourceId}`)),
      onSuccess: (resource) => cacheResourceLifecycleReadback(queryClient, resource.id, resource)
    }),
    completeUpload: useMutation({
      mutationFn: ({ resourceId, uploadId, body }: { resourceId: string; uploadId: string; body: ResourceUploadOperationRequest }) =>
        services.resources.completeUpload(resourceId, uploadId, body, createIdempotencyKey(`resources-complete-upload:${resourceId}:${uploadId}`)),
      onSuccess: (result) => invalidateArtifactReadbacks(queryClient, result.id)
    }),
    requestDownload: useMutation({
      mutationFn: ({ resourceId, uploadId, body }: { resourceId: string; uploadId: string; body?: ResourceUploadOperationRequest }) =>
        services.resources.requestDownload(resourceId, uploadId, createIdempotencyKey(`resources-request-download:${resourceId}:${uploadId}`), body),
      onSuccess: (result) => invalidateArtifactReadbacks(queryClient, result.id)
    }),
    deleteUpload: useMutation({
      mutationFn: ({ resourceId, uploadId, body }: { resourceId: string; uploadId: string; body?: ResourceUploadOperationRequest }) =>
        services.resources.deleteUpload(resourceId, uploadId, createIdempotencyKey(`resources-delete-upload:${resourceId}:${uploadId}`), body),
      onSuccess: (result) => invalidateArtifactReadbacks(queryClient, result.id)
    })
  };
}
